import { useCallback, useState } from "react";
import { useToastContext } from "../../../../common/components/ui/toast/toast.context";
import { FormikProvider, useFormik } from "formik";
import * as Yup from "yup";
import {
  preparationVehicle,
  updateVehicleProps,
} from "../../../../common/services/vehicles.api";
import { Modal } from "flowbite-react";
import PreparationVehicleCheck from "../forms/custom-fields/PreparationVehicleCheck";
import { IVehicle } from "../../../../types/vehicle.type";
import ButtonWithLoader from "../../../../common/components/ui/loading/ButtonLoading";

import "./ModalPreparationVehicle.scss";
import ModalRedoPreparationVehicle from "./ModalRedoPreparation";

type PreparationVehicleProps = {
  formRequestErrors?: { message: string; path: string[] }[];
  vehicle: IVehicle;
  onClose: () => void;
  isReadOnly?: boolean;
  type?: string;
};

export default function ModalPreparationVehicle(
  props: PreparationVehicleProps
) {
  const isReadOnly = props.isReadOnly || false;
  const title = props.type === "repreparation" ? "Repreparando" : "Preparando";
  const { notifySuccess, notifyError } = useToastContext();
  const [, setNewInitialData] = useState({
    vehiclePreparing: {
      cellphoneSupport: false,
      engineProtection: false,
      paintingProtection: false,
      phoneChargerSupport: false,
      tracker: false,
    },
  });

  const validationSchema = Yup.object().shape({
    vehiclePreparing: Yup.object().shape({
      cellphoneSupport: Yup.boolean(),
      engineProtection: Yup.boolean(),
      paintingProtection: Yup.boolean(),
      phoneChargerSupport: Yup.boolean(),
      tracker: Yup.boolean(),
      trackerValidation: Yup.boolean(),
      hasSpareKey: Yup.boolean(),
      preparation: Yup.object(),
    }),
  });

  const [showRedoPreparation, setShowRedoPreparation] = useState(false);

  const emptyObject = {
    paintingProtection: false,
    tracker: false,
    cellphoneSupport: false,
    phoneChargerSupport: false,
    engineProtection: false,
    trackerValidation: false,
    hasSpareKey: false,
  };

  const formik = useFormik({
    initialValues: {
      vehiclePreparing: props.vehicle.vehiclePreparing || {
        ...emptyObject,
        preparation: {},
        repreparation: {},
      },
    },
    validationSchema: validationSchema,
    onSubmit: async (data) => {
      if (isVehiclePreparing(data)) {
        const result = await preparationVehicle(
          props.vehicle.licensePlate,
          props.vehicle.model,
          {
            ...data.vehiclePreparing,
            repreparation: data.vehiclePreparing.repreparation ?? {},
          }
        );
        if (!result.ok) {
          notifyError(result.message as string);
          props.onClose();
          return;
        }
      }

      const result = await updateVehicleProps(
        props.vehicle._id as string,
        data
      );

      if (result.ok) {
        formik.setValues(result.data);
        setNewInitialData(result.data);
        notifySuccess("atualizado com sucesso.");
        props.onClose();
      }
      return result;
    },
  });

  const isVehiclePreparing = useCallback((data = formik.values) => {
    if (
      [
        data.vehiclePreparing.cellphoneSupport,
        data.vehiclePreparing.engineProtection,
        data.vehiclePreparing.phoneChargerSupport,
        data.vehiclePreparing.paintingProtection,
        data.vehiclePreparing.tracker,
      ].every(Boolean)
    ) {
      return true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Modal size="5xl" show={true} onClose={() => props.onClose()}>
        <FormikProvider value={formik}>
          <Modal.Header className="text-black dark:text-black modalHeaderPreparation">
            <h2 className="text-black dark:text-black">
              {title}: {props.vehicle.licensePlate}
            </h2>
          </Modal.Header>
          <Modal.Body
            className="w-full relative border-gray flex flex-col text-black"
            style={{
              minHeight: "calc(40vh - 150px)",
              height: "calc(80vh - 200px)",
            }}
          >
            {!isReadOnly && props.type !== "repreparation" && (
              <div
                className="containerButtonRedoPreparation"
                onClick={() => setShowRedoPreparation(!showRedoPreparation)}
              >
                <button className="bg-redPrimary color-white">
                  Repreparação
                </button>
              </div>
            )}
            <section className="mt-10 flex items-center justify-between p-4 dark:text-black">
              <PreparationVehicleCheck
                vehicleId={props.vehicle._id}
                isReadOnly={isReadOnly}
              />
            </section>
          </Modal.Body>
          <Modal.Footer className="text-black dark:text-black text-end w-full flex justify-end">
            <ButtonWithLoader
              disabled={isReadOnly || isVehiclePreparing() ? true : false}
              isLoading={formik.isSubmitting}
              onClick={formik.handleSubmit}
            >
              Salvar
            </ButtonWithLoader>
          </Modal.Footer>
        </FormikProvider>
        {showRedoPreparation && (
          <ModalRedoPreparationVehicle
            onClose={() => setShowRedoPreparation(false)}
            vehicle={props.vehicle}
          ></ModalRedoPreparationVehicle>
        )}
      </Modal>
    </>
  );
}
